/** @jsxImportSource @emotion/react */

import styled from "@emotion/styled";
import { LogoLong } from "uis/Logo";
import { theme } from "styles/theme";
import { css } from "@emotion/react";
import Button from "uis/Button";
import { useEffect, useState } from "react";
import AlertIcon from "uis/Icon/AlertIcon";
import ReceiveEmailModal from "./receive-email-modal";
import ReceiveEmailConfirmModal from "./receive-email-confirm-modal";
import { decrypt } from "utils/crypto";
import { useParams } from "react-router-dom";
import { getGoogleUrl } from "apis/google/getGoogleUrl";
import { sendGuideEmail } from "apis/google/sendGuideEmail";
import { gaEvent } from "utils/ga-utils";

const HeaderContainer = styled.header`
  height: 70px;
  display: flex;
  padding: 0 49px;
  align-items: center;
  background-color: white;

  border-bottom: 1px solid ${theme.neutral200};
`;

const Container = styled.div`
  width: 100%;
  height: calc(100vh - 70px);

  display: flex;
  align-items: center;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  margin-bottom: 40px;
`;
const ErrorText = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${theme.neutral600};
  line-height: 120%;
  text-align: center;
`;

const PermissionGuideContainer = styled.div`
  padding: 40px 80px;
  border: 2px solid ${theme.neutral200};
  border-radius: 24px;

  margin-bottom: 40px;
`;

const GuideContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  font-size: 16px;
  font-weight: 600;
  color: ${theme.neutral500};
  text-align: center;

  margin-bottom: 100px;
`;
const GuideLinkText = styled.a`
  color: ${theme.primary500};
  text-decoration: underline;
`;

const ActionContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
`;

export default function Error() {
  const type = decodeURIComponent(`${useParams().type}`) ?? "";
  const email = decrypt(decodeURIComponent(`${useParams().email}`)) ?? "";
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState<boolean>(false);

  const [errorType, setErrorType] = useState<"NO GWS" | "NO PERMISSION">("NO GWS");

  useEffect(() => {
    gaEvent("google-login-error");
    localStorage.clear();

    if (type === "permission") {
      setErrorType("NO PERMISSION");
    } else {
      setErrorType("NO GWS");
    }
  }, []);

  const navigateGoogleLogin = async () => {
    gaEvent("google-login-error-다시 로그인하기");

    const googleRedirectUrl = await getGoogleUrl();
    if (googleRedirectUrl) window.location.href = googleRedirectUrl;
  };

  const onClickReceiveEmail = () => {
    gaEvent("google-login-error-해당 이메일로 이용 방법 안내받기");
    setIsOpenModal(true);
  };

  const onClickDirectReceiveEmail = async (email: string) => {
    gaEvent("google-login-error-해당 이메일로 이용 방법 안내받기-즉시 안내받기");
    setIsOpenModal(false);

    const result = await sendGuideEmail(email);
    if (!result) return;

    setIsOpenConfirmModal(true);
  };

  return (
    <>
      <HeaderContainer>
        <div
          onClick={() => {
            gaEvent("google-login-error-header-logo");
            window.location.href = "/login";
          }}
          style={{ cursor: "pointer" }}
        >
          <LogoLong height="30px" />
        </div>
      </HeaderContainer>
      <Container>
        <div
          css={css`
            width: fit-content;
            height: fit-content;
            margin: auto;
          `}
        >
          <ErrorContainer>
            <AlertIcon width={80} height={80} />
            <ErrorText>
              Mile을 이용하기 위해서
              <br />
              Google Workspace 계정이 필요합니다.
            </ErrorText>
          </ErrorContainer>
          {errorType === "NO PERMISSION" ? (
            <PermissionGuideContainer>
              <div
                css={css`
                  font-size: 18px;
                  font-weight: 600;
                  color: ${theme.neutral900};
                  margin-bottom: 24px;
                `}
              >
                Mile 서비스는 구글 "최고권한관리자" 역할의 계정을 권장합니다.
              </div>
              <div
                css={css`
                  font-size: 16px;
                  font-weight: 400;
                  color: ${theme.neutral900};

                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                `}
              >
                <div>- Google 사용자 정보 읽기 권한이 필요합니다.</div>
                <div>- Google 사용자 캘린더 접근 권한이 필요합니다.</div>
                <div>- Google 디렉토리 사용자 읽기 권한이 필요합니다.</div>
                <div>- Google 디렉토리 리소스 캘린더 읽기 권한이 필요합니다.</div>
              </div>
            </PermissionGuideContainer>
          ) : (
            <></>
          )}
          <GuideContainer>
            {errorType === "NO PERMISSION" ? (
              <div>
                Google에서 Workspace 내 권한 설정 직접 알아보기{" "}
                <GuideLinkText
                  href="https://support.google.com/a/topic/2785005?hl=ko&ref_topic=2425090&sjid=10367162026590509640-AP"
                  target="_blank"
                  onClick={() => {
                    gaEvent("google-login-error-Google에서 Workspace 내 권한 설정 직접 알아보기");
                  }}
                >
                  바로가기
                </GuideLinkText>
              </div>
            ) : (
              <div>
                Google에서 Workspace 계정 직접 알아보기{" "}
                <GuideLinkText
                  href="https://workspace.google.com/intl/ko/"
                  target="_blank"
                  onClick={() => {
                    gaEvent("google-login-error-Google Workspace 계정 직접 알아보기");
                  }}
                >
                  바로가기
                </GuideLinkText>
              </div>
            )}
            <div>
              Mile 계정 생성 가이드에서 직접 알아보기{" "}
              <GuideLinkText
                href="https://mile.mintlify.app/essentials/create-auth"
                target="_blank"
                onClick={() => {
                  gaEvent("google-login-error-Mile 계정 생성 가이드에서 직접 알아보기");
                }}
              >
                바로가기
              </GuideLinkText>
            </div>
          </GuideContainer>
          <ActionContainer>
            <Button
              onClick={navigateGoogleLogin}
              title="다시 로그인하기"
              type="secondary"
              styles={{
                container: css`
                  width: 250px;
                  height: 56px;
                  background-color: ${theme.neutral100};
                  &:hover {
                    background-color: ${theme.neutral200};
                  }
                `,
                text: css`
                  font-size: 16px;
                  font-weight: 700;
                  color: ${theme.neutral900};
                `,
              }}
            />
            <Button
              onClick={onClickReceiveEmail}
              title="해당 이메일로 이용 방법 안내받기"
              type="primary"
              styles={{
                container: css`
                  width: 430px;
                  height: 56px;
                `,
                text: css`
                  font-size: 16px;
                  font-weight: 700;
                `,
              }}
            />
          </ActionContainer>
        </div>
      </Container>
      <ReceiveEmailModal
        email={email}
        modalIsOpen={isOpenModal}
        closeModal={() => setIsOpenModal(false)}
        onHandleConfirm={() => {
          onClickDirectReceiveEmail(email);
        }}
      />
      <ReceiveEmailConfirmModal
        email={email}
        modalIsOpen={isOpenConfirmModal}
        closeModal={() => setIsOpenConfirmModal(false)}
      />
    </>
  );
}
